import React, { useEffect, useRef, useState } from "react";
import { Card } from "@rumbledirect/ui";

// Reuse logic from existing component (could be factored out to a shared util if used elsewhere)
const decodeHtmlEntities = (value: string) => {
  let prev: string | null = null;
  let current = value;
  let iterations = 3;
  while (
    iterations-- > 0 &&
    current !== prev &&
    /&[a-zA-Z#0-9]+;/.test(current)
  ) {
    prev = current;
    const ta = document.createElement("textarea");
    ta.innerHTML = current;
    current = ta.value;
  }
  return current;
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const stripTwigPreserveOutputs = (value: string) =>
  value
    .replace(/{#[\s\S]*?#}/g, "")
    .replace(/{%[\s\S]*?%}/g, "")
    .replace(/\s(href|src)\s*=\s*"[^"]*{{[\s\S]*?}}[^"]*"/gi, ' $1="#"')
    .replace(/\s(href|src)\s*=\s*'[^']*{{[\s\S]*?}}[^']*'/gi, " $1='#'")
    .replace(
      /="([^"]*){{[\s\S]*?}}([^"]*)"/g,
      (_m, pre: string, post: string) => `="${pre}${post}"`,
    )
    .replace(
      /='([^']*){{[\s\S]*?}}([^']*)'/g,
      (_m, pre: string, post: string) => `='${pre}${post}'`,
    )
    .replace(/{{\s*([\s\S]*?)\s*}}/g, (_m, expr: string) => {
      const placeholder = escapeHtml(`{{${expr}}}`);
      return `<span style=\"color: #ccc;\">${placeholder}</span>`;
    });

export interface DocumentTemplatePreviewIframeProps {
  title: string;
  html?: string;
}

export const DocumentTemplatePreviewIframe: React.FC<
  DocumentTemplatePreviewIframeProps
> = ({ title, html }) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined,
  );

  // Prepare sanitized content
  let bodyContent = "";
  if (html !== undefined) {
    bodyContent = stripTwigPreserveOutputs(decodeHtmlEntities(html)).trim();
  }

  // Listen for height messages
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (
        e.data &&
        typeof e.data === "object" &&
        "__docPreviewHeight" in e.data
      ) {
        const h = Number(e.data.__docPreviewHeight);
        if (!Number.isNaN(h)) {
          setContentHeight(h);
        }
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Fallback on load if postMessage script blocked
  const handleLoad = () => {
    if (!iframeRef.current) return;
    try {
      const doc =
        iframeRef.current.contentDocument ||
        iframeRef.current.contentWindow?.document;
      if (doc) {
        const h = doc.documentElement.scrollHeight || doc.body.scrollHeight;
        if (h && (!contentHeight || Math.abs(contentHeight - h) > 5)) {
          setContentHeight(h);
        }
      }
    } catch (_) {}
  };

  return (
    <Card title={title}>
      <iframe
        ref={iframeRef}
        title={title}
        srcDoc={bodyContent}
        sandbox="allow-same-origin"
        style={{
          width: "100%",
          border: "0",
          height: contentHeight ?? 120,
          transition: "height 150ms ease-in-out",
          display: "block",
          background: "#fff",
        }}
        onLoad={handleLoad}
      />
    </Card>
  );
};

export default DocumentTemplatePreviewIframe;
